/* Talking to the builder's server. */

import type { AssetInfo, ProjectDoc, PublicConfig, PriceResult, PreflightResult, Issue } from '@gsb/core';

export class ApiError extends Error {
  constructor(message: string, public status: number, public data?: any) { super(message); }
}

async function req<T>(method: string, url: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  let r: Response;
  try {
    r = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
      keepalive: method !== 'GET' && JSON.stringify(body || '').length < 60000
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError('We can’t reach Printees right now. Check your connection.', 0);
  }
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(j.error || 'Something went wrong (' + r.status + ').', r.status, j.data);
  return j as T;
}

export interface ProjectResponse {
  id: string; rev: number; doc: ProjectDoc; status: string; updatedAt: string; assets: AssetInfo[];
  lastSubmission: SubmissionInfo | null;
  /* 0.3e Stage B: made in this browser, saved to an email (masked), part of an order */
  madeHere?: boolean; savedTo?: string | null; emailedAt?: string | null; orderedAt?: string | null; orderRef?: string | null;
}

export interface EmailSessionInfo { mode?: 'on' | 'off'; signedIn: boolean; email?: string; expiresAt?: string }
export interface EmailStart { challengeId: string; sentTo: string; resendInSec: number; expiresInSec: number; tries: number }
export interface EmailChallenge { status: 'sending' | 'sent' | 'failed'; error: string | null; message: string | null; retryAt: string | null; expired: boolean; triesLeft: number }
export interface EmailVerified {
  ok: true; purpose: 'save' | 'find'; session: EmailSessionInfo; saved: { projectId: string; savedTo: string } | null;
  confirmation: 'sent' | 'address' | 'limit' | 'unavailable' | null; confirmationMessage: string | null;
}

export interface SubmissionInfo {
  id: string; projectId: string; status: 'rendering' | 'ready' | 'failed'; progress: number; error: string | null; createdAt: string;
  price: PriceResult; priceRush: PriceResult | null;
  sheets: { index: number; widthMm: number; lengthMm: number; items: number; kind: string; fixedKey?: string }[];
  files: { sheet: number; name: string; bytes: number; widthPx: number; heightPx: number; dpi: number; url: string }[];
  previews: { sheet: number; width: number; height: number; url: string }[];
  handoffUrl: string;
  delivery: { status: string; orderId: string } | null;
}

export interface ProjectListItem {
  id: string; name: string; status: string; createdAt: string; updatedAt: string; thumbUrl: string;
  madeHere?: boolean; savedTo?: string | null; orderedAt?: string | null;
  summary: { transfer: string; designs: number; transfers: number; sheets: { widthMm: number; lengthMm: number; items: number }[]; price: number | null; utilisation: number | null };
}

export const api = {
  config: () => req<PublicConfig>('GET', '/api/config'),
  session: () => req<{ ok: true }>('POST', '/api/session', {}),
  createProject: (doc?: ProjectDoc) => req<{ id: string; rev: number; doc: ProjectDoc; savedTo?: string | null }>('POST', '/api/projects', doc ? { doc } : {}),
  project: (id: string) => req<ProjectResponse>('GET', '/api/projects/' + id),
  save: (id: string, rev: number, doc: ProjectDoc, signal?: AbortSignal) => req<{ rev: number }>('PUT', '/api/projects/' + id, { rev, doc }, signal),
  list: () => req<{ projects: ProjectListItem[] }>('GET', '/api/projects'),
  duplicate: (id: string) => req<{ id: string }>('POST', '/api/projects/' + id + '/duplicate', {}),
  remove: (id: string) => req<{ ok: true }>('DELETE', '/api/projects/' + id),
  assets: (ids: string[]) => req<{ assets: AssetInfo[] }>('GET', '/api/assets?ids=' + ids.join(',')),
  preflight: (id: string) => req<{ preflight: PreflightResult; problems: string[]; needAck: Issue[] }>('POST', '/api/projects/' + id + '/preflight', {}),
  submit: (id: string, rev: number) => req<{ submission: SubmissionInfo }>('POST', '/api/projects/' + id + '/submit', { rev }),
  submission: (id: string) => req<{ submission: SubmissionInfo }>('GET', '/api/submissions/' + id),
  adminMe: () => req<{ admin: boolean }>('GET', '/api/admin/me'),
  adminLogin: (password: string) => req<{ ok: true }>('POST', '/api/admin/login', { password }),
  adminLogout: () => req<{ ok: true }>('POST', '/api/admin/logout', {}),
  adminSettings: () => req<{ builder: any; pricing: any }>('GET', '/api/admin/settings'),
  adminSave: (body: { builder?: any; pricing?: any }) => req<{ builder: any; pricing: any }>('PUT', '/api/admin/settings', body),
  adminSubmissions: () => req<{ submissions: any[] }>('GET', '/api/admin/submissions'),
  adminStorage: () => req<StorageReport>('GET', '/api/admin/storage'),
  /* Save & Email My Gangsheet (0.3e Stage B) */
  emailSession: () => req<EmailSessionInfo>('GET', '/api/email/session'),
  emailStart: (email: string, purpose: 'save' | 'find', projectId?: string) => req<EmailStart>('POST', '/api/email/start', { email, purpose, ...(projectId ? { projectId } : {}) }),
  emailChallenge: (id: string) => req<EmailChallenge>('GET', '/api/email/challenge/' + id),
  emailVerify: (challengeId: string, code: string) => req<EmailVerified>('POST', '/api/email/verify', { challengeId, code }),
  emailSignout: (everywhere: boolean) => req<{ ok: true; ended: number }>('POST', '/api/email/signout', { everywhere }),
  saveToEmail: (id: string) => req<{ ok: true; savedTo: string; confirmation: string; confirmationMessage: string | null }>('POST', '/api/projects/' + id + '/email', {}),
  removeFromEmail: (id: string) => req<{ ok: true; stillHere: boolean }>('DELETE', '/api/projects/' + id + '/email'),
  emailSteps: (id: string) => req<{ ok: true; sentTo: string }>('POST', '/api/projects/' + id + '/email/confirm', {}),
  adminEmailStatus: () => req<any>('GET', '/api/admin/email/status'),
  adminEmailAudit: () => req<{ entries: any[] }>('GET', '/api/admin/email/audit'),
  adminEmail: (action: 'lookup' | 'clear-pause' | 'signout-all' | 'forget', body: { email: string; staffName: string; confirmEmail?: string }) => req<any>('POST', '/api/admin/email/' + action, body)
};

/** Upload one file with progress. Resolves with the new asset. */
export function uploadFile(file: File, onProgress: (fraction: number) => void, signal?: AbortSignal): Promise<AssetInfo> {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('POST', '/api/assets');
    x.withCredentials = true;
    x.responseType = 'json';
    x.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    x.onload = () => {
      const j: any = x.response || {};
      if (x.status >= 200 && x.status < 300 && j.asset) resolve(j.asset as AssetInfo);
      else reject(new ApiError(j.error || 'The upload failed (' + x.status + ').', x.status));
    };
    x.onerror = () => reject(new ApiError('The upload stopped. Check your connection and try again.', 0));
    x.onabort = () => reject(new ApiError('Upload cancelled.', 499));
    signal?.addEventListener('abort', () => x.abort());
    const fd = new FormData();
    fd.append('file', file, file.name);
    x.send(fd);
  });
}

export interface StorageReport {
  driver: 'local' | 's3';
  disks: { label: string; free: number; total: number }[];
  assets: { count: number; bytes: number; last24h: number; visitors24h: number };
  orders: number;
  minFreeGb: number;
  visitorQuotaGb: number;
  warning: string | null;
}
